import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, ArrowUp, ArrowDown, Save, SlidersHorizontal, Eye, EyeOff } from 'lucide-react';

export default function AdminSiteConfig() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Site Configuration | Admin';
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  const load = () => {
    api.adminSiteConfig()
      .then(({ data }) => setCfg({ ...data, verticals: [...(data.verticals || [])].sort((a, b) => a.nav_order - b.nav_order) }))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Admin access required</h1></div></Layout>;
  if (!cfg) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;

  const setVertical = (key, patch) => setCfg((s) => ({
    ...s, verticals: s.verticals.map((v) => (v.key === key ? { ...v, ...patch } : v)),
  }));

  const move = (idx, dir) => {
    const arr = [...cfg.verticals];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    // Re-sequence nav_order in steps of 10.
    const reseq = arr.map((v, i) => ({ ...v, nav_order: (i + 1) * 10 }));
    setCfg((s) => ({ ...s, verticals: reseq }));
  };

  const setVisibility = (key, val) => setCfg((s) => ({ ...s, visibility: { ...s.visibility, [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.updateSiteConfig({ verticals: cfg.verticals, visibility: cfg.visibility });
      setCfg({ ...data.config, verticals: [...(data.config.verticals || [])].sort((a, b) => a.nav_order - b.nav_order) });
      toast.success(`Saved. Frontend now live (v${data.config.version}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const enabledCount = cfg.verticals.filter((v) => v.enabled).length;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-site-config">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><SlidersHorizontal className="h-6 w-6 text-accent" /> Site Configuration</h1>
          <Badge variant="outline" data-testid="site-config-version">v{cfg.version}</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          The single source of truth for the storefront. Toggling a vertical here instantly updates the primary
          navigation and page availability across the site — no code changes required.
        </p>

        <Card className="p-5 rounded-2xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold">Verticals & Navigation</h2>
              <p className="text-xs text-muted-foreground">{enabledCount} of {cfg.verticals.length} verticals enabled.</p>
            </div>
          </div>
          <div className="space-y-2" data-testid="site-config-verticals">
            {cfg.verticals.map((v, idx) => (
              <div key={v.key} className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${v.enabled ? 'border-border bg-card' : 'border-dashed border-border bg-muted/40'}`} data-testid={`vertical-row-${v.key}`}>
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-accent disabled:opacity-30" data-testid={`vertical-up-${v.key}`}><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === cfg.verticals.length - 1} className="text-muted-foreground hover:text-accent disabled:opacity-30" data-testid={`vertical-down-${v.key}`}><ArrowDown className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Input value={v.label} onChange={(e) => setVertical(v.key, { label: e.target.value })} className="h-8 max-w-[180px]" data-testid={`vertical-label-${v.key}`} />
                    <code className="text-xs text-muted-foreground truncate">{v.route}</code>
                  </div>
                </div>
                {v.enabled ? <Eye className="h-4 w-4 text-accent" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                <Switch checked={!!v.enabled} onCheckedChange={(c) => setVertical(v.key, { enabled: c })} data-testid={`vertical-toggle-${v.key}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 rounded-2xl mb-6">
          <h2 className="font-semibold mb-1">Entity Visibility</h2>
          <p className="text-xs text-muted-foreground mb-4">Global switches for discovery surfaces (deals, itineraries, creators, etc.).</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="site-config-visibility">
            {Object.keys(cfg.visibility).map((key) => (
              <div key={key} className="flex items-center justify-between rounded-xl border border-border p-3">
                <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                <Switch checked={cfg.visibility[key] !== false} onCheckedChange={(c) => setVisibility(key, c)} data-testid={`visibility-toggle-${key}`} />
              </div>
            ))}
          </div>
        </Card>

        <Separator className="mb-4" />
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" className="rounded-xl" onClick={() => navigate('/admin')} data-testid="site-config-back">← Back to Admin</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="site-config-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save & publish
          </Button>
        </div>
      </div>
    </Layout>
  );
}
